import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDataContext } from 'src/contexts';
import { apiUrl } from 'src/config';

import { useSendAnalyticsEvent, useSteps } from 'src/hooks';
import { markStartRecordingSent } from 'src/utils/pvo';

const postHogEventName = 'pvo_consent_accepted';

export const useInitializationStep = () => {
  const navigate = useNavigate();

  const { sendEvent } = useSendAnalyticsEvent();
  const { nextStep } = useSteps();
  const { hash } = useParams<{ hash: string }>();
  const setIsLoading = useDataContext((ctx) => ctx.setIsLoading);

  const fetchStartRecording = async (hash: string) => {
    const data = {
      hash,
    };
    markStartRecordingSent(hash);
    const response = await fetch(`${apiUrl}/v2/pvo/start-recording`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok && response.status === 409) {
      navigate('/hash-used');
      return false;
    }

    return response.ok;
  };

  const onAcceptConsent = useCallback(async () => {
    sendEvent(postHogEventName);
    if (!hash) {
      return;
    }
    setIsLoading(true);

    try {
      const response = await fetchStartRecording(hash);

      if (!response) {
        throw new Error('Failed to start recording');
      }

      nextStep();
    } catch (error) {
      console.error('Failed to start recording', error);
    } finally {
      setIsLoading(false);
    }
  }, [hash, nextStep, setIsLoading]);

  return {
    onAcceptConsent,
  };
};
